import { CadNextBootstrapView } from '@mark43/cad-api';
import { getGlobalProperties } from '../../global-properties/global-properties';

/**
 * In Pendo, the Account ID is for the group that the user belongs to.
 * https://support.pendo.io/hc/en-us/articles/360045829772-Planning-your-Pendo-installation
 *
 * We use the Account ID to identify the tenant (subdomain or department). This prefix helps make the Account ID unique
 * per environment. Production tenants do not have a prefix because they're considered the default.
 */
function getEnvironmentPrefix(
    mark43Env: Awaited<ReturnType<typeof getGlobalProperties>>['ENVIRONMENT']
): string {
    switch (mark43Env) {
        case 'qa':
            return 'qa-';
        case 'staging':
            return 'staging-';
        default:
            return '';
    }
}

type ThemePreference = 'light' | 'dark';

function getCurrentThemePreference(): ThemePreference {
    if (typeof document !== 'undefined') {
        const domTheme = document.documentElement.getAttribute('data-theme');
        if (domTheme === 'light') {
            return 'light';
        }
    }

    return 'dark';
}

export type NarrativeDirectionPreference = 'newOnTop' | 'newOnBottom';

export function toNarrativeDirectionPendoValue(newOnTop: boolean): NarrativeDirectionPreference {
    return newOnTop ? 'newOnTop' : 'newOnBottom';
}

/**
 * Initialize Pendo.
 * https://support.pendo.io/hc/en-us/articles/360046272771-Developer-s-guide-to-installing-Pendo
 * "Call this function after users are authenticated in your app and your visitor and account id values are available"
 */
export async function initPendo(bootstrapState: CadNextBootstrapView): Promise<void> {
    const { userProfileView, cadDepartmentAgencyView, enabledFeatures } = bootstrapState;
    const globalProperties = await getGlobalProperties();
    if (window.pendo && globalProperties) {
        const themePreference = getCurrentThemePreference();
        const narrativeDirectionPreference = toNarrativeDirectionPendoValue(
            enabledFeatures.defaultNarrativeDirectionNewOnTop
        );
        const {
            departmentId,
            subDomain,
            analyticsAlias,
            departmentStatus,
        } = cadDepartmentAgencyView;
        const accountId = analyticsAlias
            ? `${analyticsAlias}-${departmentId}`
            : `${subDomain}-${departmentId}`;
        window.pendo.initialize({
            visitor: {
                id: `${userProfileView.userId.toString()}-${departmentId}`,
                themePreference,
                narrativeDirectionPreference,
            },
            account: {
                id: `${getEnvironmentPrefix(globalProperties.ENVIRONMENT)}${accountId}`,
                alias: analyticsAlias ?? null,
                tenantStatus: departmentStatus,
            },
        });
    }
}

export function updateNarrativeDirectionPendoProperty(newOnTop: boolean): void {
    if (window.pendo?.isReady?.() && window.pendo?.updateOptions) {
        window.pendo.updateOptions({
            visitor: {
                narrativeDirectionPreference: toNarrativeDirectionPendoValue(newOnTop),
            },
        });
    }
}
